import React, { ReactChild, useContext } from 'react';
import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';
import { FormContext } from 'containers/Forms/Customers/views/CreateCustomerBeta/components';

import LanguageField from './languageField';
import { SeniorField } from './seniorStatus';
import LoyaltyCardNumberField from './customerCardNumber';

type Children = ReturnType<typeof React.Children.toArray>;

const UICustomerFormBeta: Required<PosPlugin>['UICustomerFormBeta'] = ({
  customerType,
  customer,
  handleChange,
  onChange,
  children,
}) => {
  const { config } = useContext(FormContext);

  /**
   * Adds 'language' field
   */
  const addLanguageField = R.insert(
    5,
    <LanguageField
      customer={(customer as unknown) as { language: string }}
      handleChange={handleChange}
    />,
  );

  /**
   * If 'customerCardNumber' field is in the right side column,
   * swaps it with the next field so that it ends up on the left instead
   */
  const moveCardNoToLeftColumn = (fields: Children): Children => {
    /**
     * Disabled fields still exist in fields, they just render as null
     * Fields that are not present in context (e.g. added by plugin) count as enabled
     */
    const isEnabledInConfig = R.pipe(
      R.pipe(
        R.path(['props', 'name']),
        R.prop(R.__, config),
        R.either(R.prop('enabled'), R.isNil),
      ),
    );
    const visibleItems: [ReactChild, number][] = R.pipe(
      // Attach indexes of original list
      R.addIndex(R.map)((item, index) => [item, index]),
      // Filter out visible items
      R.filter(([item]) => isEnabledInConfig(item)),
    )(fields);
    const visibleIndex = visibleItems.findIndex(([item]) =>
      R.pathEq(['props', 'name'], 'customerCardNumber', item),
    );

    if (visibleIndex < 0) return fields;
    if (visibleIndex % 2 === 0) return fields; // Already in left column

    // Swap with next (or previous) visible item
    const targetIndex = visibleItems[visibleIndex][1];
    const nextIndex =
      visibleItems[visibleIndex + 1]?.[1] ??
      visibleItems[visibleIndex - 1]?.[1];
    return R.move(targetIndex, nextIndex, fields);
  };

  /**
   * Replaces 'customerCardNumber' field with our custom implementation
   */
  const replaceCardNumberField: (children: Children) => Children = R.map(
    R.when(
      R.pathEq(['props', 'name'], 'customerCardNumber'),
      R.always(
        <LoyaltyCardNumberField
          customer={customer}
          handleChange={handleChange}
          onChange={onChange}
        />,
      ),
    ),
  );

  /**
   * Adds 'Senior' checkbox
   */
  const addSeniorField = R.append(
    <SeniorField
      customer={(customer as unknown) as { senior?: 0 | 1 }}
      handleChange={handleChange}
    />,
  );

  return R.pipe(
    React.Children.toArray,
    addLanguageField,
    customerType === 'PERSON' ? addSeniorField : R.identity,
    customer.id ? R.identity : moveCardNoToLeftColumn,
    replaceCardNumberField,
  )(children);
};

export default UICustomerFormBeta;
